// Apolo IDE — PlanCanvas viewport (sprint plan-tab-v1, E2).
//
// Pan + zoom + render de blocks + connections SVG layer. Drag-to-move dos
// cards usa Framer Motion. Wheel: pan; ctrl/meta+wheel: zoom around cursor.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, MessageSquare, Sparkles } from 'lucide-react';
import { usePlanCanvas } from '../../hooks/usePlanCanvas.js';
import PlanChatPane from './PlanChatPane.jsx';
import PlanSkillsPalette from './PlanSkillsPalette.jsx';
import PlanSuggestionBar from './PlanSuggestionBar.jsx';
import PlanChartsView from './PlanChartsView.jsx';
import SprintBuilderModal from './SprintBuilderModal.jsx';
import { useAgentStore } from '../../store/useAgentStore.js';
import { usePlanFileUpload } from '../../hooks/usePlanFileUpload.js';
import {
  ZOOM_STEP,
  zoomAround, blocksBoundingBox, fitToView, connectionPath, screenToCanvas,
} from '../../lib/plan/canvasMath.js';
import { buildPresetSeries } from '../../lib/plan/chartData.js';
import { computePhaseEstimates } from '../../lib/plan/estimates.js';
import PlanCard from './PlanCard.jsx';
import PlanCanvasToolbar from './PlanCanvasToolbar.jsx';
import PlanCreateBlockMenu from './PlanCreateBlockMenu.jsx';
import PlanCanvasHeader from './PlanCanvasHeader.jsx';

const PAN_KEYS = new Set([' ', 'Spacebar']);

export default function PlanCanvas({ plan, onRenamePlan }) {
  const containerRef = useRef(null);
  const viewportRef  = useRef(null);
  const [view, setView] = useState({ panX: 200, panY: 120, zoom: 1 });
  const [spacePan, setSpacePan] = useState(false); // hold space = pan mode
  const [panning, setPanning] = useState(false);
  const panStart = useRef(null);
  const [createMenuPos, setCreateMenuPos] = useState(null);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [topZ, setTopZ] = useState(0);

  const {
    blocks, connections, loading, error, savingState,
    addBlock, patchBlock, removeBlock, updateBlockLocal,
    addConnection, removeConnection,
  } = usePlanCanvas(plan?.id);
  // Drag-to-connect: arrastar de um handle de um card até outro card.
  const [connecting, setConnecting] = useState(null);       // { fromId } | null
  const [connectCursor, setConnectCursor] = useState(null); // {x,y} canvas-space
  const [connectHoverId, setConnectHoverId] = useState(null);
  const { upload: uploadFile, uploading: uploadingFile } = usePlanFileUpload(plan?.id);
  const [dragOver, setDragOver] = useState(false);
  const planView = useAgentStore((s) => s.plan.ui.planView) || 'canvas';
  const chatOpen = useAgentStore((s) => s.plan.ui.chatOpen);
  const setPlanUiPref = useAgentStore((s) => s.setPlanUiPref);
  const [skillsOpen, setSkillsOpen] = useState(false);
  // Barra de sugestões (próximos passos). Dismiss persiste por plano.
  const suggestKey = plan?.id ? `apolo:plan:${plan.id}:suggestbar` : null;
  const [suggestDismissed, setSuggestDismissed] = useState(() => {
    try { return suggestKey ? localStorage.getItem(suggestKey) === 'hidden' : false; } catch { return false; }
  });
  function dismissSuggestions() {
    setSuggestDismissed(true);
    try { if (suggestKey) localStorage.setItem(suggestKey, 'hidden'); } catch { /* ignore */ }
  }
  function reopenSuggestions() {
    setSuggestDismissed(false);
    try { if (suggestKey) localStorage.removeItem(suggestKey); } catch { /* ignore */ }
  }

  // Preset de chart: o ChartBlockBody dispara apolo:plan:chartPreset; aqui
  // temos blocks + estimativas pra computar a série e fazer patch do bloco.
  useEffect(() => {
    function onPreset(e) {
      const { blockId, presetId } = e.detail || {};
      if (!blockId || !presetId) return;
      const target = blocks.find((b) => b.id === blockId);
      if (!target) return;
      const chart = buildPresetSeries(presetId, blocks, computePhaseEstimates(blocks));
      patchBlock(blockId, { payload: { ...target.payload, type: chart.type, title: chart.title, series: chart.series, source: chart.source } });
    }
    window.addEventListener('apolo:plan:chartPreset', onPreset);
    return () => window.removeEventListener('apolo:plan:chartPreset', onPreset);
  }, [blocks, patchBlock]);

  // Aplica uma sugestão: cria os blocos das actions no viewport visível.
  async function applySuggestion(s) {
    if (!s?.actions?.length) return;
    const rect = containerRef.current?.getBoundingClientRect();
    const cx = rect ? rect.width / 2 : 480;
    const cy = rect ? rect.height / 3 : 200;
    const base = screenToCanvas({ x: cx, y: cy, panX: view.panX, panY: view.panY, zoom: view.zoom });
    const isScaffold = s.id === 'scaffold';
    let i = 0;
    let headingX = base.x - 220;
    let headingY = base.y;
    for (const act of s.actions) {
      let x = base.x - 180;
      let y = base.y;
      if (isScaffold) {
        y = base.y + i * 120; // empilha as 6 fases
      } else if (act.kind === 'heading') {
        x = headingX; y = headingY;
      } else if (act.kind === 'skill') {
        x = headingX + 40; y = headingY + 70; // skill logo abaixo do heading
      }
      await addBlock({ kind: act.kind, x: Math.round(x), y: Math.round(y), payload: act.payload });
      i++;
    }
  }
  const [builderOpen, setBuilderOpen] = useState(false);

  // Keep topZ in sync with max(z) of current blocks.
  useEffect(() => {
    if (!blocks || blocks.length === 0) { setTopZ(0); return; }
    const m = blocks.reduce((acc, b) => Math.max(acc, b.z ?? 0), 0);
    setTopZ(m);
  }, [blocks]);

  // Space key holds for pan mode.
  // BUG (2026-07-04): o listener é de WINDOW e fazia preventDefault em TODO espaço —
  // com o Plan montado (surfaces persistentes), a barra de espaço morria em TODOS os
  // inputs do app (composer, busca…). Alvo editável = digitação, nunca pan.
  useEffect(() => {
    const editable = (t) => !!t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable);
    function down(e) { if (editable(e.target)) return; if (PAN_KEYS.has(e.key)) { e.preventDefault(); setSpacePan(true); } }
    function up(e)   { if (PAN_KEYS.has(e.key)) setSpacePan(false); }
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => { window.removeEventListener('keydown', down); window.removeEventListener('keyup', up); };
  }, []);

  // Wheel: pan (default), ctrl/meta = zoom around cursor.
  const handleWheel = useCallback((e) => {
    if (!containerRef.current) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const rect = containerRef.current.getBoundingClientRect();
      const pivotX = e.clientX - rect.left;
      const pivotY = e.clientY - rect.top;
      const factor = e.deltaY > 0 ? 1 - ZOOM_STEP : 1 + ZOOM_STEP;
      setView((v) => zoomAround({ pivotX, pivotY, currentZoom: v.zoom, nextZoom: v.zoom * factor, panX: v.panX, panY: v.panY }));
    } else {
      setView((v) => ({ ...v, panX: v.panX - e.deltaX, panY: v.panY - e.deltaY }));
    }
  }, []);

  // Mouse pan with space-hold OR middle button.
  function handleMouseDown(e) {
    const isMiddle = e.button === 1;
    if (!(spacePan || isMiddle)) return;
    if (e.target?.closest?.('[data-block-id]')) return;
    e.preventDefault();
    setPanning(true);
    panStart.current = { x: e.clientX, y: e.clientY, panX: view.panX, panY: view.panY };
  }
  function handleMouseMove(e) {
    if (!panning || !panStart.current) return;
    const dx = e.clientX - panStart.current.x;
    const dy = e.clientY - panStart.current.y;
    setView((v) => ({ ...v, panX: panStart.current.panX + dx, panY: panStart.current.panY + dy }));
  }
  function handleMouseUp() {
    setPanning(false);
    panStart.current = null;
  }

  // Click vazio: limpa seleção. Double-click vazio: abre create menu.
  function handleBackgroundClick(e) {
    if (e.target !== viewportRef.current && e.target !== containerRef.current) return;
    setSelectedBlockId(null);
    setCreateMenuPos(null);
  }
  function handleBackgroundDoubleClick(e) {
    if (!containerRef.current) return;
    if (e.target?.closest?.('[data-block-id]')) return;
    const rect = containerRef.current.getBoundingClientRect();
    const screen = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    setCreateMenuPos({ screen, canvas: screenToCanvas({ x: screen.x, y: screen.y, panX: view.panX, panY: view.panY, zoom: view.zoom }) });
  }

  function handleDragOver(e) {
    if (!e.dataTransfer) return;
    const hasFiles = Array.from(e.dataTransfer.items || []).some((it) => it.kind === 'file');
    if (!hasFiles) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    if (!dragOver) setDragOver(true);
  }
  function handleDragLeave(e) {
    // Only clear when leaving the container itself (not children).
    if (e.target === containerRef.current) setDragOver(false);
  }
  async function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (!containerRef.current || !e.dataTransfer?.files) return;
    const rect = containerRef.current.getBoundingClientRect();
    const screen = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    const canvasPos = screenToCanvas({ x: screen.x, y: screen.y, panX: view.panX, panY: view.panY, zoom: view.zoom });
    const files = Array.from(e.dataTransfer.files);
    let offsetX = 0;
    for (const file of files) {
      const isPdf = file.type === 'application/pdf' || file.name?.toLowerCase().endsWith('.pdf');
      const isImg = file.type?.startsWith('image/');
      const ox = offsetX;
      if (isImg && file.size <= 3_000_000) {
        // Imagem pequena: dataURL inline (preview imediato, sem upload).
        const reader = new FileReader();
        reader.onload = async () => {
          await addBlock({ kind: 'image', x: canvasPos.x - 180 + ox, y: canvasPos.y - 120, payload: { url: reader.result, alt: file.name } });
        };
        reader.readAsDataURL(file);
      } else {
        // Qualquer outro formato (pdf, docx, xlsx, zip, mp4, imagem grande…):
        // cria placeholder + upload R2 genérico em background.
        const kind = isPdf ? 'pdf' : 'file';
        const placeholder = await addBlock({
          kind,
          x: canvasPos.x - 200 + ox,
          y: canvasPos.y - (isPdf ? 270 : 70),
          payload: { name: file.name, file_id: null },
        });
        const result = await uploadFile(file);
        if (result.ok && placeholder?.block?.id) {
          await patchBlock(placeholder.block.id, {
            kind: result.kind || kind,
            payload: { file_id: result.fileId, name: result.name || file.name, size_bytes: result.size_bytes, mime: result.mime },
          });
        }
      }
      offsetX += 40;
    }
  }

  function bumpZ(blockId) {
    const next = topZ + 1;
    setTopZ(next);
    updateBlockLocal(blockId, { z: next });
  }

  // Inicia uma conexão a partir do handle de um card.
  function handleConnectStart(fromId) {
    setSelectedBlockId(null);
    setCreateMenuPos(null);
    setConnecting({ fromId });
  }

  // Enquanto conectando: linha segue o cursor + detecta card alvo sob o mouse.
  useEffect(() => {
    if (!connecting) return undefined;
    function move(e) {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      setConnectCursor(screenToCanvas({
        x: e.clientX - rect.left, y: e.clientY - rect.top,
        panX: view.panX, panY: view.panY, zoom: view.zoom,
      }));
      const card = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-block-id]');
      const id = card?.getAttribute('data-block-id');
      setConnectHoverId(id && id !== connecting.fromId ? id : null);
    }
    function up(e) {
      const card = document.elementFromPoint(e.clientX, e.clientY)?.closest?.('[data-block-id]');
      const toId = card?.getAttribute('data-block-id');
      if (toId && toId !== connecting.fromId) {
        addConnection({ from_block_id: connecting.fromId, to_block_id: toId });
      }
      setConnecting(null);
      setConnectCursor(null);
      setConnectHoverId(null);
    }
    function esc(e) { if (e.key === 'Escape') { setConnecting(null); setConnectCursor(null); setConnectHoverId(null); } }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('keydown', esc);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('keydown', esc);
    };
  }, [connecting, view.panX, view.panY, view.zoom, addConnection]);

  // Zoom controls callbacks (toolbar).
  const zoomIn  = useCallback(() => setView((v) => zoomAround({ pivotX: rectCenter(containerRef).x, pivotY: rectCenter(containerRef).y, currentZoom: v.zoom, nextZoom: v.zoom + ZOOM_STEP, panX: v.panX, panY: v.panY })), []);
  const zoomOut = useCallback(() => setView((v) => zoomAround({ pivotX: rectCenter(containerRef).x, pivotY: rectCenter(containerRef).y, currentZoom: v.zoom, nextZoom: v.zoom - ZOOM_STEP, panX: v.panX, panY: v.panY })), []);
  const resetZoom = useCallback(() => setView({ panX: 200, panY: 120, zoom: 1 }), []);
  const fit = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const bbox = blocksBoundingBox(blocks);
    if (!bbox) { setView({ panX: rect.width / 2, panY: rect.height / 2, zoom: 1 }); return; }
    setView(fitToView({ bbox, viewportW: rect.width, viewportH: rect.height, padding: 80 }));
  }, [blocks]);

  // Keyboard shortcuts: ⌘+/⌘-/⌘0, ⌘J pra toggle chat
  useEffect(() => {
    function handler(e) {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key === '=' || e.key === '+') { e.preventDefault(); zoomIn(); }
      else if (e.key === '-')              { e.preventDefault(); zoomOut(); }
      else if (e.key === '0')              { e.preventDefault(); resetZoom(); }
      else if (e.key === 'j' || e.key === 'J') {
        if (e.target?.tagName === 'INPUT' || e.target?.tagName === 'TEXTAREA' || e.target?.isContentEditable) return;
        e.preventDefault();
        setPlanUiPref('chatOpen', !chatOpen);
      }
      else if (e.shiftKey && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSkillsOpen((v) => !v);
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [zoomIn, zoomOut, resetZoom, chatOpen, setPlanUiPref]);

  // Delete selected with Backspace/Delete (only when canvas has focus).
  useEffect(() => {
    function handler(e) {
      if (!selectedBlockId) return;
      if (e.key !== 'Backspace' && e.key !== 'Delete') return;
      if (e.target && (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable)) return;
      e.preventDefault();
      removeBlock(selectedBlockId);
      setSelectedBlockId(null);
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedBlockId, removeBlock]);

  const cursor = panning ? 'grabbing' : (spacePan ? 'grab' : 'default');

  // SVG connections layer
  const connectionPaths = useMemo(() => {
    const byId = Object.fromEntries(blocks.map((b) => [b.id, b]));
    return connections
      .map((c) => {
        const from = byId[c.from_block_id];
        const to = byId[c.to_block_id];
        if (!from || !to) return null;
        return { id: c.id, d: connectionPath(from, to), style: c.style, label: c.label };
      })
      .filter(Boolean);
  }, [blocks, connections]);

  return (
    <div className="flex flex-1 flex-col">
      <PlanCanvasHeader
        plan={plan}
        savingState={savingState}
        blockCount={blocks.length}
        onRename={onRenamePlan}
        onOpenSprintBuilder={() => setBuilderOpen(true)}
        planView={planView}
        onPlanViewChange={(v) => setPlanUiPref('planView', v)}
      />
      {planView === 'charts' ? <PlanChartsView plan={plan} blocks={blocks} /> : null}
      {planView !== 'charts' && !suggestDismissed ? (
        <PlanSuggestionBar plan={plan} blocks={blocks} onApply={applySuggestion} onDismiss={dismissSuggestions} />
      ) : null}
      <div
        ref={containerRef}
        className={`relative flex-1 overflow-hidden bg-apolo-bg ${planView === 'charts' ? 'hidden' : ''}`}
        style={{
          cursor,
          backgroundImage: 'radial-gradient(circle, var(--apolo-borderSubtle) 1px, transparent 1px)',
          backgroundSize: `${24 * view.zoom}px ${24 * view.zoom}px`,
          backgroundPosition: `${view.panX}px ${view.panY}px`,
        }}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onClick={handleBackgroundClick}
        onDoubleClick={handleBackgroundDoubleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Viewport: transformed inner container holds blocks + connections */}
        <div
          ref={viewportRef}
          className="absolute left-0 top-0"
          style={{
            transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`,
            transformOrigin: '0 0',
            width: 0,
            height: 0,
          }}
        >
          {/* SVG layer: connections clicáveis (deletar) + linha temporária. */}
          <svg
            className="pointer-events-none absolute"
            style={{ left: -10000, top: -10000, width: 20000, height: 20000, overflow: 'visible' }}
          >
            <defs>
              <marker id="plan-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6" markerHeight="6" orient="auto">
                <path d="M 0 0 L 10 5 L 0 10 z" fill="var(--apolo-plan-accent)" />
              </marker>
            </defs>
            <g transform="translate(10000,10000)">
              {connectionPaths.map((cp) => (
                <g key={cp.id} style={{ pointerEvents: connecting ? 'none' : 'auto' }}>
                  {/* Hit area larga + invisível pra clicar fácil */}
                  <path
                    d={cp.d}
                    fill="none"
                    stroke="transparent"
                    strokeWidth={16}
                    style={{ cursor: 'pointer' }}
                    onClick={(e) => { e.stopPropagation(); removeConnection(cp.id); }}
                  >
                    <title>Clique para remover esta conexão</title>
                  </path>
                  {/* Linha visível */}
                  <path
                    d={cp.d}
                    fill="none"
                    stroke="var(--apolo-plan-accent)"
                    strokeWidth={2}
                    strokeDasharray={cp.style === 'dashed' ? '6 4' : undefined}
                    strokeOpacity={0.55}
                    markerEnd="url(#plan-arrow)"
                    className="transition-[stroke-opacity] hover:!stroke-[3]"
                    style={{ pointerEvents: 'none' }}
                  />
                </g>
              ))}

              {/* Linha temporária durante o drag-to-connect */}
              {connecting && connectCursor ? (() => {
                const from = blocks.find((b) => b.id === connecting.fromId);
                if (!from) return null;
                const x0 = from.x + from.w / 2;
                const y0 = from.y + from.h / 2;
                return (
                  <path
                    d={`M ${x0},${y0} L ${connectCursor.x},${connectCursor.y}`}
                    fill="none"
                    stroke="var(--apolo-plan-accent)"
                    strokeWidth={2.5}
                    strokeDasharray="7 5"
                    markerEnd="url(#plan-arrow)"
                    style={{ pointerEvents: 'none' }}
                  />
                );
              })() : null}
            </g>
          </svg>

          {blocks.map((block) => (
            <PlanCard
              key={block.id}
              block={block}
              zoom={view.zoom}
              selected={block.id === selectedBlockId}
              onSelect={() => { setSelectedBlockId(block.id); bumpZ(block.id); }}
              onPositionChange={(pos) => updateBlockLocal(block.id, { x: Math.round(pos.x), y: Math.round(pos.y) })}
              onSizeChange={(size) => updateBlockLocal(block.id, { w: Math.max(160, size.w), h: Math.max(80, size.h) })}
              onPatch={(fields) => patchBlock(block.id, fields)}
              onRemove={() => { removeBlock(block.id); setSelectedBlockId(null); }}
              onConnectStart={handleConnectStart}
              connectMode={!!connecting && connectHoverId === block.id}
            />
          ))}
        </div>

        {/* Loading veil */}
        <AnimatePresence>
          {loading ? (
            <motion.div
              key="canvas-loading"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute inset-0 flex items-center justify-center"
            >
              <span className="flex items-center gap-2 rounded-full border border-apolo-plan-accentBorder bg-apolo-surface1/80 px-3 py-1.5 text-xs text-apolo-textSecondary backdrop-blur">
                <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                Carregando canvas…
              </span>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Drag overlay */}
        <AnimatePresence>
          {dragOver ? (
            <motion.div
              key="drop-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center"
              style={{ background: 'var(--apolo-plan-accentMuted)' }}
            >
              <div className="rounded-2xl border-2 border-dashed border-apolo-plan-accent bg-apolo-surface1/80 px-6 py-4 text-center backdrop-blur-md">
                <p className="text-base font-semibold text-apolo-plan-accent">Solte aqui</p>
                <p className="text-xs text-apolo-textSecondary">PDF, imagem ou link viram blocos no canvas.</p>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>

        {/* Upload progress */}
        {uploadingFile ? (
          <div className="absolute right-4 top-4 z-20 inline-flex items-center gap-2 rounded-full border border-apolo-plan-accentBorder bg-apolo-surface1/90 px-3 py-1.5 text-xs text-apolo-plan-accent backdrop-blur">
            <Loader2 size={12} className="animate-spin" aria-hidden="true" />
            Subindo arquivo…
          </div>
        ) : null}

        {/* Error banner */}
        {error ? (
          <div className="absolute left-4 top-4 z-20 max-w-sm rounded-md border border-apolo-error/40 bg-apolo-errorBg/80 px-3 py-2 text-xs text-apolo-error backdrop-blur">
            {error}
          </div>
        ) : null}

        {/* Toolbar inferior */}
        <PlanCanvasToolbar
          zoom={view.zoom}
          onZoomIn={zoomIn}
          onZoomOut={zoomOut}
          onResetZoom={resetZoom}
          onFit={fit}
          onOpenSkills={() => setSkillsOpen(true)}
          onAdd={() => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (!rect) return;
            const center = { x: rect.width / 2, y: rect.height / 2 };
            setCreateMenuPos({
              screen: center,
              canvas: screenToCanvas({ x: center.x, y: center.y, panX: view.panX, panY: view.panY, zoom: view.zoom }),
            });
          }}
        />

        {/* Floating create menu */}
        {createMenuPos ? (
          <PlanCreateBlockMenu
            screenPos={createMenuPos.screen}
            onClose={() => setCreateMenuPos(null)}
            onPick={async (kind) => {
              await addBlock({ kind, x: createMenuPos.canvas.x - 100, y: createMenuPos.canvas.y - 40 });
              setCreateMenuPos(null);
            }}
          />
        ) : null}

        {/* Reabrir barra de sugestões quando dismissada */}
        {suggestDismissed ? (
          <button
            type="button"
            onClick={reopenSuggestions}
            aria-label="Mostrar próximos passos sugeridos"
            title="Mostrar sugestões de próximos passos"
            className="absolute left-4 top-4 z-20 inline-flex h-9 items-center gap-1.5 rounded-full border border-apolo-plan-accentBorder bg-apolo-surface1/90 px-3 text-xs font-semibold text-apolo-plan-accent backdrop-blur-md transition hover:brightness-110"
            style={{ boxShadow: 'var(--apolo-plan-glow-tight)' }}
          >
            <Sparkles size={13} aria-hidden="true" />
            Sugestões
          </button>
        ) : null}

        {/* Chat copiloto trigger (sempre visível quando fechado) */}
        {!chatOpen ? (
          <button
            type="button"
            onClick={() => setPlanUiPref('chatOpen', true)}
            aria-label="Abrir copiloto"
            title="Abrir copiloto (⌘J)"
            className="absolute right-4 top-4 z-20 inline-flex h-10 items-center gap-1.5 rounded-full border border-apolo-plan-accentBorder bg-apolo-surface1/90 px-3.5 text-xs font-semibold text-apolo-plan-accent backdrop-blur-md transition hover:brightness-110"
            style={{ boxShadow: 'var(--apolo-plan-glow-tight)' }}
          >
            <MessageSquare size={14} aria-hidden="true" />
            Copiloto
          </button>
        ) : null}

        {/* Skills palette */}
        <PlanSkillsPalette
          open={skillsOpen}
          onClose={() => setSkillsOpen(false)}
          onPick={async (skill) => {
            const rect = containerRef.current?.getBoundingClientRect();
            const cx = rect ? rect.width / 2 : 400;
            const cy = rect ? rect.height / 2 : 300;
            const c = screenToCanvas({ x: cx, y: cy, panX: view.panX, panY: view.panY, zoom: view.zoom });
            await addBlock({
              kind: 'skill',
              x: c.x - 160,
              y: c.y - 80,
              payload: { skill_name: skill.name, version: 'latest', notes: skill.short, _phase: skill.phase, _color: skill.color },
            });
          }}
        />

        {/* Sprint Builder modal */}
        <SprintBuilderModal
          open={builderOpen}
          onClose={() => setBuilderOpen(false)}
          plan={plan}
          blocks={blocks}
          connections={connections}
        />

        {/* Chat copiloto pane */}
        <PlanChatPane
          open={chatOpen}
          onClose={() => setPlanUiPref('chatOpen', false)}
          plan={plan}
          blocks={blocks}
          onCreateBlock={async (sg) => {
            // Posiciona próximo do centro do viewport atual.
            const rect = containerRef.current?.getBoundingClientRect();
            const cx = rect ? rect.width / 2 : 400;
            const cy = rect ? rect.height / 2 : 300;
            const c = screenToCanvas({ x: cx, y: cy, panX: view.panX, panY: view.panY, zoom: view.zoom });
            await addBlock({ kind: sg.kind, x: c.x - 160, y: c.y - 100, payload: sg.payload });
          }}
        />
      </div>
    </div>
  );
}

function rectCenter(ref) {
  const el = ref.current;
  if (!el) return { x: 0, y: 0 };
  const r = el.getBoundingClientRect();
  return { x: r.width / 2, y: r.height / 2 };
}
